import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react'
import { cn } from '@app/lib/cn'
import type { ColorMapping, SizeMapping } from './api'

/**
 * Size and colour mappings (Model_B2B / Model_R2R).
 *
 * Korgun keeps these on entirely separate tabs: the quantity written on the
 * recipe belongs to a nominal size, and it's these mappings that actually
 * decide which size/colour of the material gets drawn (docs/korgun-screens.md
 * §3 pain point 2). We show them right next to the quantity.
 */

export interface MappingChipProps {
  label: string
  total: number
  different: number
  open: boolean
  onToggle: () => void
  controls: string
}

/** Small expandable chip inside a recipe line. */
export function MappingChip({ label, total, different, open, onToggle, controls }: MappingChipProps) {
  // A mapping isn't an error, it's information: steel (info) tone for emphasis;
  // amber is reserved for actual gaps (empty process, unit mismatch).
  const highlighted = different > 0
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className={cn(
        'inline-flex items-center gap-1 rounded-xs border px-1.5 py-px text-micro font-medium whitespace-nowrap transition-colors',
        highlighted
          ? 'border-celik/25 bg-celik/8 text-celik hover:bg-celik/14'
          : 'border-hairline bg-kagit text-ink-muted hover:border-ink-muted/45 hover:text-ink',
      )}
      title={
        highlighted
          ? `${different} satırda farklı ${label.toLowerCase()} çekiliyor`
          : `${total} satırın tamamı birebir eşleşiyor`
      }
    >
      <span>{label}</span>
      <span className="font-mono tnum">{highlighted ? `${different}/${total}` : total}</span>
      <ChevronDownIcon
        aria-hidden
        className={cn('size-3 transition-transform', open && 'rotate-180')}
      />
    </button>
  )
}

const sizeNumber = (value: string | null): number => {
  const parsed = Number.parseInt((value ?? '').replace(/\D/g, ''), 10)
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
}

/**
 * Size mapping strip: the model's own size on top, the size drawn from the
 * material underneath. If the two sides differ, the column is marked in the
 * steel tone.
 */
export function SizeMappingStrip({ rows }: { rows: readonly SizeMapping[] }) {
  const sorted = [...rows].sort(
    (a, b) => sizeNumber(a.size1) - sizeNumber(b.size1) || a.bedKod1 - b.bedKod1,
  )

  return (
    <div className="max-w-full overflow-x-auto rounded-xs border border-hairline">
      <table className="border-collapse font-mono tnum text-data">
        <caption className="sr-only">Model bedeni ve malzeme bedeni eşlemesi</caption>
        <tbody>
          <tr className="bg-kagit">
            <th
              scope="row"
              className="h-6 whitespace-nowrap border-b border-r border-hairline px-2 text-left font-sans text-micro font-normal text-ink-muted"
            >
              Model bedeni
            </th>
            {sorted.map((row) => (
              <td
                key={`m-${row.bedKod1}-${row.bedKod2}`}
                className={cn(
                  'h-6 min-w-9 border-b border-hairline px-2 text-center text-ink-muted',
                  row.different && 'bg-celik/8',
                )}
              >
                {row.size1 ?? row.bedKod1}
              </td>
            ))}
          </tr>
          <tr className="bg-surface">
            <th
              scope="row"
              className="h-7 whitespace-nowrap border-r border-hairline px-2 text-left font-sans text-micro font-normal text-ink-muted"
            >
              Malzeme bedeni
            </th>
            {sorted.map((row) => (
              <td
                key={`h-${row.bedKod1}-${row.bedKod2}`}
                className={cn(
                  'h-7 min-w-9 px-2 text-center font-medium text-ink',
                  row.different && 'bg-celik/8 text-celik',
                )}
              >
                {row.size2 ?? row.bedKod2}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/** Colour mapping: shoe colour → material colour pairs. */
export function ColorMappingList({ rows }: { rows: readonly ColorMapping[] }) {
  // In 51-colour models the list shouldn't take over the page, so it scrolls on its own.
  return (
    <ul
      className={cn(
        'grid grid-cols-1 gap-1 sm:grid-cols-2 xl:grid-cols-3',
        rows.length > 24 && 'max-h-72 overflow-y-auto pr-1',
      )}
    >
      {rows.map((row) => (
        <li
          key={`${row.rkod1}-${row.rkod2}`}
          className={cn(
            'flex items-center gap-1.5 rounded-xs border px-2 py-1 text-data',
            row.different ? 'border-celik/25 bg-celik/6' : 'border-hairline bg-kagit',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-ink" title={row.color1 ?? undefined}>
            {row.color1 ?? `#${row.rkod1}`}
          </span>
          <ArrowRightIcon className="size-3 shrink-0 text-ink-muted" aria-hidden />
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-right',
              row.different ? 'font-medium text-celik' : 'text-ink-muted',
            )}
            title={row.color2 ?? undefined}
          >
            {row.color2 ?? `#${row.rkod2}`}
          </span>
        </li>
      ))}
    </ul>
  )
}
